import React, {FC, useEffect, useMemo, useState} from "react";
import {Alert, Empty, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {
    EventActivityAction,
    EventActivityLogsQuery,
    EventActivityLogsQueryVariables,
    SortEnumType,
    useEventActivityLogsQuery
} from "../../GraphQL/generated/graphql";
import {formatLocalDateTime} from "./adminDateFormat";
import {StatusTag} from "../../Components/ui/StatusTag";
import type {StatusTone} from "../../Components/ui/StatusTag";
import {PageHeader, Panel} from "../../Components/ui";

const PAGE_SIZE = 25;

type EventAuditSortColumn = "occurredAt" | "action" | "userName" | "entity";
type EventAuditSortOrder = "ascend" | "descend";
export type EventAuditSortState = {
    columnKey: EventAuditSortColumn;
    order: EventAuditSortOrder;
};

const DEFAULT_SORT: EventAuditSortState = {
    columnKey: "occurredAt",
    order: "descend"
};
const SORTABLE_COLUMNS = new Set<EventAuditSortColumn>(["occurredAt", "action", "userName", "entity"]);

export const buildEventAuditOrder = (
    sortState: EventAuditSortState = DEFAULT_SORT
): NonNullable<EventActivityLogsQueryVariables["order"]> => {
    const direction = sortState.order === "ascend" ? SortEnumType.Asc : SortEnumType.Desc;

    switch (sortState.columnKey) {
        case "action":
            return [{action: direction}, {occurredAt: SortEnumType.Desc}, {id: SortEnumType.Desc}];
        case "userName":
            return [{userName: direction}, {occurredAt: SortEnumType.Desc}, {id: SortEnumType.Desc}];
        case "entity":
            return [
                {entityName: direction},
                {entityId: direction},
                {occurredAt: SortEnumType.Desc},
                {id: SortEnumType.Desc}
            ];
        case "occurredAt":
        default:
            return [{occurredAt: direction}, {id: direction}];
    }
};

export const resolveEventAuditSortState = (sorter: {
    columnKey?: React.Key;
    order?: EventAuditSortOrder | null;
} | Array<{
    columnKey?: React.Key;
    order?: EventAuditSortOrder | null;
}>): EventAuditSortState => {
    const resolvedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const columnKey = resolvedSorter?.columnKey;
    if (typeof columnKey !== "string" || !SORTABLE_COLUMNS.has(columnKey as EventAuditSortColumn)) {
        return DEFAULT_SORT;
    }

    if (resolvedSorter.order !== "ascend" && resolvedSorter.order !== "descend") {
        return DEFAULT_SORT;
    }

    return {
        columnKey: columnKey as EventAuditSortColumn,
        order: resolvedSorter.order
    };
};

type EventActivityLog = NonNullable<
    NonNullable<EventActivityLogsQuery["eventActivityLogs"]>["items"]
>[number];

const parseEventId = (value?: string) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const ACTION_TONES: Record<EventActivityAction, StatusTone> = {
    [EventActivityAction.Created]: "success",
    [EventActivityAction.Updated]: "info",
    [EventActivityAction.Deleted]: "error",
    [EventActivityAction.Archived]: "warning",
    [EventActivityAction.Restored]: "info",
    [EventActivityAction.Unknown]: "neutral"
};

const ACTION_LABELS: Record<EventActivityAction, {key: string; defaultValue: string} | undefined> = {
    [EventActivityAction.Created]: {key: "event-audit.action.created", defaultValue: "Created"},
    [EventActivityAction.Updated]: {key: "event-audit.action.updated", defaultValue: "Updated"},
    [EventActivityAction.Deleted]: {key: "event-audit.action.deleted", defaultValue: "Deleted"},
    [EventActivityAction.Archived]: {key: "event-audit.action.archived", defaultValue: "Archived"},
    [EventActivityAction.Restored]: {key: "event-audit.action.restored", defaultValue: "Restored"},
    [EventActivityAction.Unknown]: undefined
};

const normalizeDetails = (
    entry: EventActivityLog,
    translate: ReturnType<typeof useTranslation>["t"]
) => {
    const details = entry.details ?? "";
    if (entry.actionType !== EventActivityAction.Archived) {
        return details;
    }

    const archivedPrefix = translate("event-audit.event-archived-details-prefix", {
        defaultValue: "Event archived"
    });

    // Legacy event archives were stored as generic deletes.
    return details.replace(/^(Obrisan zapis|Deleted record)/i, archivedPrefix);
};

const EventAuditPage: FC = () => {
    const {t} = useTranslation();
    const {eventId} = useParams();
    const numericEventId = parseEventId(eventId);
    const [page, setPage] = useState(1);
    const [sortState, setSortState] = useState<EventAuditSortState>(DEFAULT_SORT);

    useEffect(() => {
        setPage(1);
        setSortState(DEFAULT_SORT);
    }, [numericEventId]);

    const order = useMemo(() => buildEventAuditOrder(sortState), [sortState]);

    const query = useEventActivityLogsQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        skip: !numericEventId,
        variables: {
            eventId: numericEventId ?? 0,
            order,
            skip: (page - 1) * PAGE_SIZE,
            take: PAGE_SIZE
        }
    });

    const columnSortOrder = (columnKey: EventAuditSortColumn) =>
        sortState.columnKey === columnKey ? sortState.order : undefined;
    const columns = useMemo<TableColumnsType<EventActivityLog>>(() => [
        {
            dataIndex: "occurredAt",
            key: "occurredAt",
            title: t("event-audit.time", {defaultValue: "Time"}),
            width: 180,
            sorter: true,
            sortOrder: columnSortOrder("occurredAt"),
            render: value => formatLocalDateTime(value)
        },
        {
            dataIndex: "action",
            key: "action",
            title: t("event-audit.action", {defaultValue: "Action"}),
            width: 130,
            sorter: true,
            sortOrder: columnSortOrder("action"),
            render: (action, entry) => {
                const label = ACTION_LABELS[entry.actionType];

                return (
                    <StatusTag tone={ACTION_TONES[entry.actionType] ?? "neutral"}>
                        {label ? t(label.key, {defaultValue: label.defaultValue}) : action}
                    </StatusTag>
                );
            }
        },
        {
            dataIndex: "userName",
            key: "userName",
            title: t("event-audit.user", {defaultValue: "User"}),
            width: 210,
            sorter: true,
            sortOrder: columnSortOrder("userName"),
            render: userName => userName || <Typography.Text type="secondary">-</Typography.Text>
        },
        {
            key: "entity",
            title: t("event-audit.entity", {defaultValue: "Entity / ID"}),
            width: 230,
            sorter: true,
            sortOrder: columnSortOrder("entity"),
            render: (_, entry) => (
                <Typography.Text>{entry.entityName} / {entry.entityId}</Typography.Text>
            )
        },
        {
            dataIndex: "details",
            key: "details",
            title: t("event-audit.details", {defaultValue: "Details"}),
            render: (details, entry) => normalizeDetails(entry, t)
                || <Typography.Text type="secondary">-</Typography.Text>
        }
    ], [sortState, t]);

    // antd also calls this on page changes; only a sort change resets to the first page.
    const handleTableChange = (
        _: unknown,
        __: unknown,
        sorter: Parameters<typeof resolveEventAuditSortState>[0],
        extra: {action: string}
    ) => {
        if (extra.action !== "sort") {
            return;
        }

        setSortState(resolveEventAuditSortState(sorter));
        setPage(1);
    };

    if (!numericEventId) {
        return (
            <Alert
                message={t("event-workspace.invalid-event", {defaultValue: "Invalid event"})}
                type="error"
                showIcon
            />
        );
    }

    const collection = query.data?.eventActivityLogs;
    const items = collection?.items ?? [];
    const totalCount = collection?.totalCount ?? 0;

    return (
        <div className="ati-workspace-page">
            <PageHeader level={2} title={t("event-workspace.audit", {defaultValue: "Audit"})}/>
            <Panel>
            {query.error ? (
                <Alert
                    description={t("event-audit.load-error-description", {
                        defaultValue: "Refresh the page or try again later."
                    })}
                    message={t("event-audit.load-error", {
                        defaultValue: "Audit could not be loaded"
                    })}
                    showIcon
                    type="error"
                />
            ) : (
                <Table<EventActivityLog>
                    columns={columns}
                    dataSource={items}
                    loading={query.loading}
                    locale={{
                        emptyText: (
                            <Empty
                                description={t("event-audit.empty", {
                                    defaultValue: "No changes have been recorded for this event."
                                })}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                    }}
                    onChange={handleTableChange}
                    pagination={{
                        current: page,
                        onChange: nextPage => setPage(nextPage),
                        pageSize: PAGE_SIZE,
                        showSizeChanger: false,
                        total: totalCount
                    }}
                    rowKey="id"
                    scroll={{x: 950}}
                />
            )}
        </Panel>
        </div>
    );
};

export default EventAuditPage;
